import React, { useState } from "react";
import axios from "axios";

const ExploreAgent = ({
  API_BASE,
  exploreUrl,
  setExploreUrl,
  exploreUsername,
  setExploreUsername,
  explorePassword,
  setExplorePassword,
  isExploring,
  setIsExploring,
  explorationScreenshots,
  setExplorationScreenshots,
  runExplorationAgent
}) => {
  const [filterQuery, setFilterQuery] = useState("");
  const [maxDepth, setMaxDepth] = useState(3);
  const [captureDelay, setCaptureDelay] = useState(2);
  const [selectedScreenshot, setSelectedScreenshot] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);

  // Local handler if runExplorationAgent is executed directly from this page
  const handleStartExploration = async (e) => {
    if (e) e.preventDefault();
    if (runExplorationAgent) {
      runExplorationAgent();
    } else {
      if (!exploreUrl.trim()) {
        alert("Please enter the target application URL to explore.");
        return;
      }
      setIsExploring(true);
      setExplorationScreenshots([]);
      setSelectedFiles([]);
      try {
        const res = await axios.post(`${API_BASE}/explore`, {
          base_url: exploreUrl.trim(),
          username: exploreUsername.trim(),
          password: explorePassword.trim(),
          max_depth: maxDepth,
          delay: captureDelay
        });
        if (res.data.success) {
          setExplorationScreenshots(res.data.screenshots || []);
          alert(` Web exploration completed! ${res.data.screenshots ? res.data.screenshots.length : 0} screenshots captured.`);
        } else {
          alert(`✗ Exploration failed: ${res.data.error || "Unknown error"}`);
        }
      } catch (err) {
        alert(`✗ Error running exploration agent: ${err.message}`);
      } finally {
        setIsExploring(false);
      }
    }
  };

  const filteredScreenshots = explorationScreenshots.filter((item) => {
    if (!filterQuery) return true;
    const q = filterQuery.toLowerCase();
    return (
      (item.name && item.name.toLowerCase().includes(q)) ||
      (item.route && item.route.toLowerCase().includes(q)) ||
      (item.filename && item.filename.toLowerCase().includes(q))
    );
  });

  // Checkbox selection handlers
  const toggleSelectFile = (filename) => {
    if (selectedFiles.includes(filename)) {
      setSelectedFiles(selectedFiles.filter((f) => f !== filename));
    } else {
      setSelectedFiles([...selectedFiles, filename]);
    }
  };

  const handleSelectAll = () => {
    setSelectedFiles(filteredScreenshots.map((item) => item.filename));
  };

  const handleDeselectAll = () => {
    setSelectedFiles([]);
  };

  // Batch download selected files into a single ZIP archive folder
  const handleBatchDownload = async () => {
    if (selectedFiles.length === 0) {
      alert("Please check at least one screenshot to download.");
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/v1/screenshots/download-zip`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ filenames: selectedFiles })
      });
      if (!response.ok) throw new Error("Zip endpoint returned error");
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = "exploration_screenshots.zip";
      document.body.appendChild(link);
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(link);
    } catch (err) {
      console.warn("Falling back to URL zip download:", err);
      const url = `${API_BASE}/api/v1/screenshots/download-zip?filenames=${encodeURIComponent(selectedFiles.join(","))}`;
      window.location.href = url;
    }
  };

  return (
    <div style={{ padding: "24px", maxWidth: "1400px", margin: "0 auto", textAlign: "left" }}>
      {/* Page Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
          background: "#ffffff",
          padding: "20px 24px",
          borderRadius: "16px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          border: "1px solid #f1f5f9"
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "12px",
                background: "#FD6101",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#ffffff",
                fontSize: "20px"
              }}
            >
              
            </div>
            <div>
              <h2 style={{ margin: 0, fontSize: "22px", fontWeight: "800", color: "#0f172a" }}>
                Application Exploration Agent
              </h2>
              <p style={{ margin: "2px 0 0 0", fontSize: "13px", color: "#64748b" }}>
                Autonomous visual web crawler & route mapping engine. Map out UI routes, test access control, and capture live screenshots.
              </p>
            </div>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <span
            style={{
              padding: "6px 14px",
              borderRadius: "20px",
              fontSize: "12px",
              fontWeight: "700",
              background: isExploring ? "#fef3c7" : explorationScreenshots.length > 0 ? "#ecfdf5" : "#f1f5f9",
              color: isExploring ? "#d97706" : explorationScreenshots.length > 0 ? "#059669" : "#64748b",
              border: `1px solid ${isExploring ? "#fde68a" : explorationScreenshots.length > 0 ? "#a7f3d0" : "#e2e8f0"}`
            }}
          >
            {isExploring ? " Exploring Routes..." : explorationScreenshots.length > 0 ? `✓ ${explorationScreenshots.length} Screenshots Captured` : " Idle"}
          </span>
        </div>
      </div>

      {/* Main Grid Layout */}
      <div style={{ display: "grid", gridTemplateColumns: "360px 1fr", gap: "24px" }}>
        {/* Left Column: Configuration & Controls */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Target URL & Auth Card */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              padding: "20px",
              border: "1px solid #f1f5f9",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}
          >
            <h3 style={{ fontSize: "16px", fontWeight: "700", margin: "0 0 16px 0", color: "#0f172a" }}>
               Exploration Settings
            </h3>

            <form onSubmit={handleStartExploration} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#334155", marginBottom: "6px" }}>
                  Target Web Application URL <span style={{ color: "#ef4444" }}>*</span>
                </label>
                <input
                  type="url"
                  placeholder="https://app.example.com"
                  value={exploreUrl}
                  onChange={(e) => setExploreUrl(e.target.value)}
                  disabled={isExploring}
                  required
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    outline: "none"
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#334155", marginBottom: "6px" }}>
                  Login Username / Email (Optional)
                </label>
                <input
                  type="text"
                  placeholder="admin@example.com"
                  value={exploreUsername}
                  onChange={(e) => setExploreUsername(e.target.value)}
                  disabled={isExploring}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    outline: "none"
                  }}
                />
              </div>

              <div>
                <label style={{ display: "block", fontSize: "12px", fontWeight: "700", color: "#334155", marginBottom: "6px" }}>
                  Login Password (Optional)
                </label>
                <input
                  type="password"
                  placeholder="••••••••••••"
                  value={explorePassword}
                  onChange={(e) => setExplorePassword(e.target.value)}
                  disabled={isExploring}
                  style={{
                    width: "100%",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: "1px solid #cbd5e1",
                    fontSize: "13px",
                    outline: "none"
                  }}
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                <div>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: "700", color: "#475569", marginBottom: "4px" }}>
                    Max Depth
                  </label>
                  <select
                    value={maxDepth}
                    onChange={(e) => setMaxDepth(Number(e.target.value))}
                    disabled={isExploring}
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "12px"
                    }}
                  >
                    <option value={1}>1 Level (Shallow)</option>
                    <option value={3}>3 Levels (Standard)</option>
                    <option value={5}>5 Levels (Deep Crawl)</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "11px", fontWeight: "700", color: "#475569", marginBottom: "4px" }}>
                    Screen Delay (s)
                  </label>
                  <select
                    value={captureDelay}
                    onChange={(e) => setCaptureDelay(Number(e.target.value))}
                    disabled={isExploring}
                    style={{
                      width: "100%",
                      padding: "8px 10px",
                      borderRadius: "6px",
                      border: "1px solid #cbd5e1",
                      fontSize: "12px"
                    }}
                  >
                    <option value={1}>1 second</option>
                    <option value={2}>2 seconds</option>
                    <option value={4}>4 seconds</option>
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={isExploring}
                style={{
                  marginTop: "8px",
                  width: "100%",
                  padding: "12px 18px",
                  borderRadius: "10px",
                  border: "none",
                  background: isExploring ? "#94a3b8" : "#FD6101",
                  color: "#ffffff",
                  fontSize: "14px",
                  fontWeight: "700",
                  cursor: isExploring ? "not-allowed" : "pointer",
                  boxShadow: isExploring ? "none" : "0 4px 12px rgba(253, 97, 1, 0.25)",
                  transition: "all 0.2s ease"
                }}
                onMouseEnter={e => { if (!isExploring) e.currentTarget.style.background = "#e05300"; }}
                onMouseLeave={e => { if (!isExploring) e.currentTarget.style.background = "#FD6101"; }}
              >
                {isExploring ? " Exploration In Progress..." : " Start Autonomous Exploration"}
              </button>
            </form>
          </div>

          {/* How It Works Guide */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              padding: "20px",
              border: "1px solid #f1f5f9",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}
          >
            <h4 style={{ fontSize: "14px", fontWeight: "700", margin: "0 0 10px 0", color: "#0f172a" }}>
               How Explore Agent Works
            </h4>
            <ul style={{ margin: 0, paddingLeft: "18px", fontSize: "12px", color: "#64748b", lineHeight: "1.7" }}>
              <li>Launches an automated Playwright headless web browser.</li>
              <li>Authenticates using provided login credentials (if required).</li>
              <li>Traverses all discovered application navigation links & routes.</li>
              <li>Captures high-resolution visual screenshots of every page state.</li>
              <li>Select items using checkboxes and click <strong>Download Selected</strong> to export.</li>
            </ul>
          </div>
        </div>

        {/* Right Column: Exploration Results & Screenshot Gallery */}
        <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
          {/* Controls Bar & Filter & Checkbox Download Action */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              padding: "16px 20px",
              border: "1px solid #f1f5f9",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "12px"
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px", flexWrap: "wrap" }}>
              {filteredScreenshots.length > 0 && (
                <>
                  <button
                    onClick={handleSelectAll}
                    disabled={selectedFiles.length === filteredScreenshots.length}
                    style={{
                      padding: "8px 14px",
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      background: "#f8fafc",
                      color: selectedFiles.length === filteredScreenshots.length ? "#94a3b8" : "#334155",
                      fontSize: "13px",
                      fontWeight: "700",
                      cursor: selectedFiles.length === filteredScreenshots.length ? "default" : "pointer"
                    }}
                  >
                    ☑️ Select All ({filteredScreenshots.length})
                  </button>

                  <button
                    onClick={handleDeselectAll}
                    disabled={selectedFiles.length === 0}
                    style={{
                      padding: "8px 14px",
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      background: "#f8fafc",
                      color: selectedFiles.length > 0 ? "#ef4444" : "#94a3b8",
                      fontSize: "13px",
                      fontWeight: "700",
                      cursor: selectedFiles.length > 0 ? "pointer" : "not-allowed"
                    }}
                  >
                    ☐ Deselect All
                  </button>
                </>
              )}

              {/* Batch Download Button */}
              <button
                onClick={handleBatchDownload}
                disabled={selectedFiles.length === 0}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "none",
                  background: selectedFiles.length > 0 ? "#10b981" : "#e2e8f0",
                  color: selectedFiles.length > 0 ? "#ffffff" : "#94a3b8",
                  fontSize: "13px",
                  fontWeight: "700",
                  cursor: selectedFiles.length > 0 ? "pointer" : "not-allowed",
                  boxShadow: selectedFiles.length > 0 ? "0 2px 8px rgba(16, 185, 129, 0.25)" : "none",
                  transition: "all 0.2s ease"
                }}
                onMouseEnter={e => { if (selectedFiles.length > 0) e.currentTarget.style.background = "#059669"; }}
                onMouseLeave={e => { if (selectedFiles.length > 0) e.currentTarget.style.background = "#10b981"; }}
              >
                ⬇️ Download Selected ({selectedFiles.length})
              </button>
            </div>

            <input
              type="text"
              placeholder=" Search captured routes or page names..."
              value={filterQuery}
              onChange={(e) => setFilterQuery(e.target.value)}
              style={{
                padding: "8px 14px",
                borderRadius: "8px",
                border: "1px solid #cbd5e1",
                fontSize: "12px",
                width: "260px"
              }}
            />
          </div>

          {/* Screenshot Gallery Grid */}
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              padding: "24px",
              border: "1px solid #f1f5f9",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              minHeight: "450px"
            }}
          >
            {isExploring ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "60px 20px",
                  textAlign: "center"
                }}
              >
                <div
                  style={{
                    width: "48px",
                    height: "48px",
                    border: "4px solid #fff7ed",
                    borderTop: "4px solid #FD6101",
                    borderRadius: "50%",
                    animation: "spin 1s linear infinite",
                    marginBottom: "16px"
                  }}
                />
                <h4 style={{ margin: "0 0 6px 0", color: "#0f172a", fontSize: "16px", fontWeight: "700" }}>
                  Autonomous Web Agent Active
                </h4>
                <p style={{ margin: 0, color: "#64748b", fontSize: "13px" }}>
                  Navigating application routes, analyzing DOM structures, and taking page screenshots…
                </p>
              </div>
            ) : filteredScreenshots.length === 0 ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "80px 20px",
                  textAlign: "center"
                }}
              >
                <div style={{ fontSize: "42px", marginBottom: "12px" }}></div>
                <h4 style={{ margin: "0 0 6px 0", color: "#0f172a", fontSize: "16px", fontWeight: "700" }}>
                  No Exploration Screenshots Yet
                </h4>
                <p style={{ margin: "0 0 16px 0", color: "#64748b", fontSize: "13px", maxWidth: "420px" }}>
                  Enter your target application URL on the left panel and click <strong>" Start Autonomous Exploration"</strong> to automatically map and capture your UI.
                </p>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "16px" }}>
                {filteredScreenshots.map((item, idx) => {
                  const isChecked = selectedFiles.includes(item.filename);
                  return (
                    <div
                      key={idx}
                      style={{
                        border: isChecked ? "2px solid #FD6101" : "1px solid #e2e8f0",
                        borderRadius: "12px",
                        overflow: "hidden",
                        background: isChecked ? "#fff7ed" : "#f8fafc",
                        transition: "all 0.2s ease",
                        position: "relative"
                      }}
                    >
                      {/* Checkbox Selector Badge Top Left */}
                      <div
                        style={{
                          position: "absolute",
                          top: "10px",
                          left: "10px",
                          zIndex: 10,
                          background: "rgba(15, 23, 42, 0.75)",
                          padding: "4px 8px",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          gap: "6px",
                          backdropFilter: "blur(4px)"
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => toggleSelectFile(item.filename)}
                          style={{ width: "16px", height: "16px", accentColor: "#FD6101", cursor: "pointer" }}
                        />
                        <span style={{ fontSize: "11px", fontWeight: "700", color: "#ffffff" }}>
                          {isChecked ? "Selected" : "Select"}
                        </span>
                      </div>

                      {/* Thumbnail */}
                      <div
                        style={{
                          position: "relative",
                          paddingTop: "56.25%",
                          background: "#0f172a",
                          cursor: "pointer"
                        }}
                        onClick={() => setSelectedScreenshot(item)}
                      >
                        <img
                          src={`${API_BASE}/screenshots/${item.filename}`}
                          alt={item.name}
                          style={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            objectFit: "cover"
                          }}
                        />
                      </div>

                      {/* Metadata & Actions */}
                      <div style={{ padding: "14px" }}>
                        <div style={{ fontSize: "10px", fontWeight: "800", textTransform: "uppercase", color: "#FD6101" }}>
                          #{item.index || idx + 1} {item.route || "/"}
                        </div>
                        <div
                          style={{
                            fontSize: "14px",
                            fontWeight: "700",
                            color: "#0f172a",
                            marginTop: "4px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {item.name || `Page Screenshot ${idx + 1}`}
                        </div>

                        <div style={{ display: "flex", gap: "10px", alignItems: "center", marginTop: "12px" }}>
                          <a
                            href={`${API_BASE}/screenshots/${item.filename}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{
                              fontSize: "12px",
                              color: "#FD6101",
                              textDecoration: "none",
                              fontWeight: "600"
                            }}
                          >
                             Open Full
                          </a>
                          <a
                            href={`${API_BASE}/screenshots/${item.filename}`}
                            download={item.filename || "exploration_screenshot.png"}
                            style={{
                              fontSize: "12px",
                              color: "#059669",
                              textDecoration: "none",
                              fontWeight: "600"
                            }}
                          >
                            ⬇️ Download
                          </a>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Lightbox Preview Modal */}
      {selectedScreenshot && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(15, 23, 42, 0.8)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 99999,
            padding: "20px"
          }}
          onClick={() => setSelectedScreenshot(null)}
        >
          <div
            style={{
              background: "#ffffff",
              borderRadius: "16px",
              maxWidth: "1000px",
              width: "100%",
              maxHeight: "90vh",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "16px 20px", display: "flex", justifyContent: "space-between", borderBottom: "1px solid #e2e8f0" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: "16px", fontWeight: "700" }}>{selectedScreenshot.name}</h3>
                <span style={{ fontSize: "12px", color: "#64748b" }}>{selectedScreenshot.route}</span>
              </div>
              <button
                onClick={() => setSelectedScreenshot(null)}
                style={{ background: "none", border: "none", fontSize: "20px", cursor: "pointer", color: "#64748b" }}
              >
                ✕
              </button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "20px", background: "#0f172a", textAlign: "center" }}>
              <img
                src={`${API_BASE}/screenshots/${selectedScreenshot.filename}`}
                alt={selectedScreenshot.name}
                style={{ maxWidth: "100%", maxHeight: "70vh", borderRadius: "8px" }}
              />
            </div>
            <div style={{ padding: "14px 20px", display: "flex", justifyContent: "flex-end", gap: "12px", borderTop: "1px solid #e2e8f0" }}>
              <a
                href={`${API_BASE}/screenshots/${selectedScreenshot.filename}`}
                target="_blank"
                rel="noreferrer"
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  border: "1px solid #cbd5e1",
                  color: "#334155",
                  textDecoration: "none",
                  fontSize: "13px",
                  fontWeight: "600"
                }}
              >
                Open in New Tab
              </a>
              <a
                href={`${API_BASE}/screenshots/${selectedScreenshot.filename}`}
                download={selectedScreenshot.filename}
                style={{
                  padding: "8px 16px",
                  borderRadius: "8px",
                  background: "#059669",
                  color: "#ffffff",
                  textDecoration: "none",
                  fontSize: "13px",
                  fontWeight: "600"
                }}
              >
                ⬇️ Download Image
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExploreAgent;

